import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import {
  MutationResult,
  classifyMutationResult,
  extractResponsePayload,
} from '../../../utils/api-response';

/**
 * Surfacing API failures from the admin-portal companies flows.
 *
 * THE TWO DIALECTS — WHICH ONE DECIDES
 * ------------------------------------
 * core-api returns its `AppResponse` envelope directly from controllers, so the
 * HTTP status is almost always 200 and the real outcome lives in the body. This
 * module used to read the body's `httpStatus` string ('OK' / 'NOT_FOUND' / ...)
 * to decide that outcome. It no longer does, and neither should any new code:
 * `src/utils/api-response.ts` (the `errorCode` envelope) is CANONICAL for
 * body-level business outcomes — SUCCESS iff `errorCode === '0'`. The body's
 * `httpStatus` field is decoration and is populated inconsistently; the two
 * dialects disagree outright on a 1030 reply
 * (`{ errorCode: '1030', httpStatus: 'OK', message, details, data: '' }`),
 * which is a BODY_FAILURE.
 *
 * What stays here is the narrower, legitimate job this module always did well:
 * turning a TRANSPORT-shaped error — a thrown / `{ error }` FetchBaseQueryError,
 * i.e. FETCH_ERROR, PARSING_ERROR, a real 4xx/5xx — into human-readable copy.
 *
 * `payload.details` IS NEVER RETURNED FROM ANY BRANCH OF THIS MODULE
 * -----------------------------------------------------------------
 * `details` is where core-api puts EXCEPTION-DERIVED text
 * (GenerateExceptionAppResponse), so it can carry a JDK stack-trace message, a
 * SQL fragment, an internal hostname or a driver error. Three helpers below
 * used to fall through to it when `message` was absent, which piped raw server
 * internals into the companies pages' toasts (BasicInfo, ContactInfo,
 * MemberInfo, SubscriptionPlan). Every one of those fallthroughs is gone:
 * message-or-generic only, in every branch. The generic strings are unchanged,
 * so a failure that already had a `message` reads exactly as it did before —
 * only the raw-internals path is closed. Do not re-add a `details` read here;
 * if a failure needs more specific copy, give it a `message` in core-api or a
 * fixed string at the call site.
 *
 * WHICH HELPER TO CALL
 * --------------------
 *   - `getBodyFailureMessage(body)` — for a call site that awaited
 *     `.unwrap()`, so it holds the ENVELOPE itself. Returns null on a
 *     body-level success, otherwise the message to show.
 *   - `getMutationFailureMessage(response)` — for a call site that awaited the
 *     trigger WITHOUT `.unwrap()`, so it holds `{ data }` or `{ error }`.
 *     Covers both failure kinds in one call.
 *   - `getApiErrorMessage(error)` — for a `catch (error)` block, unchanged.
 *
 * This repo has NO global error reporting (`baseQueryWithReAuth` inspects
 * nothing in bodies), so a failure that is not reported at the call site is
 * invisible to the admin. Report every one of them.
 */

type AppResponseLike = {
  httpStatus?: string;
  message?: string | null;
  /**
   * Present on the wire and declared here so the shape stays honest — but
   * deliberately NEVER read by this module. See the header comment.
   */
  details?: string | null;
  data?: unknown;
};

const GENERIC_BODY_FAILURE_MESSAGE =
  'The server rejected the request. Please try again.';

/**
 * `details` is skipped on purpose (see the header). `error` is kept because it
 * is RTK Query's OWN transport string on a FETCH_ERROR ('Failed to fetch' /
 * 'TypeError: ...'), not server-authored text.
 */
const messageFromBody = (body: unknown): string | undefined => {
  if (body && typeof body === 'object') {
    const b = body as AppResponseLike & { error?: string };
    return b.message || b.error || undefined;
  }
  return undefined;
};

/**
 * @deprecated Reads the body's `httpStatus`, which is DECORATION on a bare
 * AppResponse and cannot decide success or failure — it calls a 1030 failure
 * body ("httpStatus": "OK") a success unless `data` happens to be falsy. Kept
 * only so nothing silently loses a behaviour during the migration; every call
 * site in this module now uses `getBodyFailureMessage`. Do not use in new code.
 */
export const getAppResponseError = (
  response: AppResponseLike | undefined
): string | null => {
  if (response?.httpStatus && response.httpStatus !== 'OK') {
    return (
      messageFromBody(response) ?? `Request failed (${response.httpStatus}).`
    );
  }
  if (!response?.data) {
    return messageFromBody(response) ?? 'The server returned an empty response.';
  }
  return null;
};

/** Extracts a human-readable message from a *thrown* RTK Query error. */
export const getApiErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'status' in error) {
    const err = error as FetchBaseQueryError;
    const fromData = messageFromBody((err as { data?: unknown }).data);
    if (fromData) return fromData;

    if (err.status === 'FETCH_ERROR')
      return 'Network error — could not reach the server.';
    if (err.status === 'PARSING_ERROR')
      return 'The server returned an unexpected response.';
    if (typeof err.status === 'number')
      return `Request failed (HTTP ${err.status}).`;
  }
  return 'Something went wrong. Please try again.';
};

/**
 * Classify an AppResponse envelope that a call site already `.unwrap()`ed.
 * Returns null for a body-level SUCCESS (`errorCode === '0'`) and a message to
 * show for anything else — including the `data: ''` / `data: null` failure
 * replies, which the old httpStatus dialect could only catch by accident.
 *
 * Transport failures do NOT reach here: `.unwrap()` throws on those, so they
 * stay in the caller's existing `catch` with `getApiErrorMessage`.
 */
export const getBodyFailureMessage = (body: unknown): string | null => {
  // Re-wrap into RTK Query's `{ data: envelope }` shape, which is what the
  // canonical classifier documents and reads.
  const response = { data: body };

  if (classifyMutationResult(response) === MutationResult.SUCCESS) return null;

  const payload = extractResponsePayload(response);
  // message-or-generic. NOT payload.details — see the header comment.
  return payload.message || GENERIC_BODY_FAILURE_MESSAGE;
};

/**
 * One-call failure copy for a call site that awaited the mutation trigger
 * WITHOUT `.unwrap()` — i.e. it holds `{ data: envelope }` on an answered
 * request and `{ error }` on a network/non-2xx failure. Only call it once the
 * caller has established the result is not a SUCCESS.
 */
export const getMutationFailureMessage = (response: unknown): string => {
  if (classifyMutationResult(response) === MutationResult.TRANSPORT_FAILURE) {
    const transportError =
      response && typeof response === 'object' && 'error' in response
        ? (response as { error: unknown }).error
        : response;
    return getApiErrorMessage(transportError);
  }

  const payload = extractResponsePayload(response);
  // message-or-generic. NOT payload.details — see the header comment.
  return payload.message || GENERIC_BODY_FAILURE_MESSAGE;
};
